import { getMessage } from '@/shared/copy/messages';
import { ApiError, type JsonValue } from '@/shared/data/client';
import { getErrorMessage } from '@/shared/data/errors';
import { authUamErrorKinds } from '@flowqa/shared';

type ClerkInviteErrorEntry = {
  code?: string;
  long_message?: string;
  longMessage?: string;
  message?: string;
};

type ClerkInviteErrorPayload = {
  errors?: ClerkInviteErrorEntry[];
};

const normalizeMessage = (value: string): string => value.trim().toLowerCase();

const parseClerkInvitePayload = (message: string): ClerkInviteErrorPayload | null => {
  const jsonStart = message.indexOf('{');
  if (jsonStart < 0) {
    return null;
  }

  const rawPayload = message.slice(jsonStart).trim();
  try {
    return JSON.parse(rawPayload) as ClerkInviteErrorPayload;
  } catch {
    return null;
  }
};

const resolveClerkInviteMessage = (payload: ClerkInviteErrorPayload): string | null => {
  const firstError = payload.errors?.[0];
  if (!firstError) {
    return null;
  }

  switch (firstError.code) {
    case 'already_a_member_in_organization':
      return getMessage('organisation.invites.alreadyMember');
    case 'unsupported_content_type':
      return getMessage('organisation.invites.cancelFailed');
    case undefined:
      return firstError.long_message ?? firstError.longMessage ?? firstError.message ?? null;
    default:
      return firstError.long_message ?? firstError.longMessage ?? firstError.message ?? null;
  }
};

const resolveApiInviteMessage = (apiError: ApiError): string | null => {
  const normalized = normalizeMessage(apiError.message);
  const details = apiError.details;
  const detailsKind =
    typeof details === 'object' && details !== null && 'kind' in details ? details.kind : null;

  if (
    detailsKind === authUamErrorKinds.membershipSyncFailed ||
    normalized.includes('organisation membership sync requires repair')
  ) {
    return getMessage('organisation.members.syncRepairRequired');
  }

  if (normalized.includes('user already belongs to this organisation')) {
    return getMessage('organisation.invites.alreadyMember');
  }

  if (normalized.includes('invites are not enabled on the solo plan')) {
    return getMessage('organisation.invites.disabled.soloPlan');
  }

  if (normalized.includes('invites are not enabled on the free plan')) {
    return getMessage('organisation.invites.disabled.freePlan');
  }

  if (normalized.includes('invites are not enabled because this plan has ended')) {
    return getMessage('organisation.invites.disabled.planEnded');
  }

  if (normalized.includes('user limit reached')) {
    return getMessage('organisation.members.userLimitReached');
  }

  if (normalized.includes('your account is not ready to send invites yet')) {
    return getMessage('organisation.invites.senderAccountNotReady');
  }

  if (normalized.includes('this workspace link does not include your account')) {
    return getMessage('organisation.invites.workspaceLinkMissingAccount');
  }

  if (normalized.includes('this workspace is not fully set up for invitations yet')) {
    return getMessage('organisation.invites.workspaceSetupRequired');
  }

  if (normalized.includes('multiple linked workspaces match this workspace for your account')) {
    return getMessage('organisation.invites.workspaceLinkConflict');
  }

  return null;
};

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

export const normalizeInviteError = (err: unknown): ApiError => {
  const apiError = toApiError(err);
  const payload = parseClerkInvitePayload(apiError.message);
  const resolvedMessage = payload ? resolveClerkInviteMessage(payload) : null;
  const finalMessage = resolvedMessage ?? resolveApiInviteMessage(apiError);

  if (!finalMessage) {
    return apiError;
  }

  return new ApiError(finalMessage, {
    status: apiError.status,
    code: apiError.code,
    details: apiError.details as JsonValue | undefined,
    errorId: apiError.errorId,
    correlationId: apiError.correlationId,
  });
};
